import React, { useState } from 'react'
import {
	change, Field, getFormValues, InjectedFormProps, reduxForm
} from 'redux-form'
import {
	Button, Col, Form, notification, Row, Select, Upload
} from 'antd'
import { SaveOutlined, UploadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FORMS } from '../../../utils/enums'
import TextField from '../../../atoms/form/TextField'
import InputField from '../../../atoms/InputField'
import validateSettingsForm from './validateSettingsForm'
import { ISettingsDetail } from '../../../reducers/settings/actions'
import { uploadPrivateFile } from '../../../utils/uploadPrivateFile'

const { Option } = Select

type ComponentProps = {
}

// "id": 1,
// 	"firmName": "UMYJEME TOP ŽA 2, s.r.o.",
// 	"firmAddress": "Festivalová 2303/17, 010 01 Žilina",
// 	"firmICO": "55117988",
// 	"firmDIC": "2121872379",
// 	"firmImageStampUrl": "https://firebasestorage.googleapis.com/v0/b/we-clean-top.appspot.com/o/price_offer_parts%2FPE%C4%8CIATKA%20-%20UMYJEME%20TOP%20ZA%202%20FOJTIK.png?alt=media&token=07b409a3-90c0-47f9-bc9c-46661bee27d3",
// 	"webPage": "www.umyjeme.top",
// 	"phone": "+421 948 444 443",
// 	"email": "zilina@umyjeme.top"

type Props = InjectedFormProps<ISettingsDetail, ComponentProps> & ComponentProps

const SettingsForm = (props: Props) => {
	const {
		handleSubmit, invalid, pristine
	} = props

	const dispatch = useDispatch()

	const { t } = useTranslation('components')

	const settingsDetail: ISettingsDetail = useSelector((state) => getFormValues(FORMS.SETTINGS_FORM)(state)) as ISettingsDetail

	const [signaturePreview, setSignaturePreview] = useState<string | null>(null)
	const [uploading, setUploading] = useState(false)

	const handleSignatureUpload = async (file: File) => {
		setUploading(true)
		try {
			const { fileId } = await uploadPrivateFile(file, file.name)
			dispatch(change(FORMS.SETTINGS_FORM, 'ceoSignatureFileID', fileId))
			// Vrateny fileUrl je verejna GCS adresa nad privatnym bucketom - v prehliadaci by dala 403.
			// Nahlad preto berieme z lokalneho suboru, kym sa po ulozeni nenacita podpisana ceoSignatureUrl.
			setSignaturePreview(URL.createObjectURL(file))
			notification.success({ message: 'Podpis konateľa bol nahraný. Nezabudnite uložiť nastavenia.' })
		} catch (e) {
			notification.error({ message: 'Podpis konateľa sa nepodarilo nahrať' })
		} finally {
			setUploading(false)
		}
	}

	return (
		<Form layout={'vertical'} onSubmitCapture={ handleSubmit }>
			<Row gutter={ 16 }>
				<Col span={ 18 } className={ 'grid' }>
					<div className={ 'flex direction-col justify-start main-content' }>
						<h1>{ t('pages:Nastavenia aplikácie') }</h1>
						<Field
							name={ 'name' }
							component={ InputField }
							label={ t('components:Názov organizácie') }
							size={ 'large' }
							required
						/>
						<Field
							name={ 'ceoName' }
							component={ InputField }
							label={ <div> <span>{t('components:Meno konateľa')} <span className={'font-normal'}>(potrebné v preberacom protokole)</span></span></div> }
							size={ 'large' }
						/>
						<Form.Item label={<span>Podpis konateľa <span className={'font-normal'}>(vkladá sa do podpísaného preberacieho protokolu)</span></span>}>
							{(signaturePreview || settingsDetail?.ceoSignatureUrl) && (
								<img
									src={signaturePreview || settingsDetail?.ceoSignatureUrl || ''}
									alt={'Podpis konateľa'}
									style={{
										maxHeight: 90, maxWidth: 260, display: 'block', marginBottom: 8, border: '1px solid #f0f0f0', padding: 4, background: '#fff'
									}}
								/>
							)}
							<Upload
								accept={'image/png,image/jpeg'}
								showUploadList={false}
								beforeUpload={(file) => { handleSignatureUpload(file); return false }}
							>
								<Button icon={<UploadOutlined />} loading={uploading}>
									{settingsDetail?.ceoSignatureFileID ? 'Nahradiť podpis' : 'Nahrať podpis'}
								</Button>
							</Upload>
							{settingsDetail?.ceoSignatureFileID && (
								<Button
									type={'link'}
									danger
									onClick={() => { dispatch(change(FORMS.SETTINGS_FORM, 'ceoSignatureFileID', null)); setSignaturePreview(null) }}
								>
									Odstrániť podpis
								</Button>
							)}
						</Form.Item>
					</div>
				</Col>
				<Col span={6} className={'grid'}>
					<div className={'flex direction-col justify-start sidebar-content' }>
						<Field
							name={ 'updatedAt' }
							component={ TextField }
							disabled
							label={ t('components:Dátum poslednej úpravy') }
						/>

						<div className={ 'flex direction-col justify-center' }>
							<Button
								icon={ <SaveOutlined /> }
								onClick={ handleSubmit }
								disabled={ pristine || invalid }
								type={ 'primary' }
								className={ 'mt-4'}
							>
								{ t('pages:Uložiť') }
							</Button>
						</div>
					</div>
				</Col>
			</Row>
			{settingsDetail?.headers?.map((header, index) => (
				<Row key={index} gutter={16} className={'mt-5'}>
					<Col span={ 24 } className={ 'grid' }>
						<div style={ { height: '100%' } } className={ 'flex direction-col justify-start main-content' }>
							<h3>{header?.firmName}</h3>
							<Field
								name={ `headers.[${index}].firmName` }
								component={ InputField }
								label={ t('components:Email organizácie') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].firmAddress` }
								component={ InputField }
								label={ t('components:Adresa organizácie') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].firmICO` }
								component={ InputField }
								label={ t('components:ICO') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].firmDIC` }
								component={ InputField }
								label={ t('components:DIC') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].firmImageStampUrl` }
								component={ InputField }
								label={ t('components:URL obrazku') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].webPage` }
								component={ InputField }
								label={ t('components:Web stránka') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].phone` }
								component={ InputField }
								label={ t('components:Telefón') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].email` }
								component={ InputField }
								label={ t('components:Email') }
								size={ 'large' }
							/>
							<h3>{t('components:Preberací protokol - podklady')}</h3>
							<Field
								name={ `headers.[${index}].bankConnection` }
								component={ InputField }
								label={ t('components:Bankové spojenie ( IBAN + meno banky)') }
								size={ 'large' }
							/>
							<Field
								name={ `headers.[${index}].companyRegistryInfo` }
								component={ InputField }
								label={ t('components:Obchodný register, kde je spoločnosť zapísaná') }
								size={ 'large' }
							/>
						</div>
					</Col>
				</Row>))
			}
		</Form>
	)
}

const form = reduxForm<ISettingsDetail, ComponentProps>({
	form: FORMS.SETTINGS_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateSettingsForm
})(SettingsForm)

export default form
